import { type ComponentType, type ReactElement } from 'react';
import { StackedMetricsStatusChartSection } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import { getChartTitle } from '@/shared/copy/charts';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { Stack } from '@/ui';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { MetricsStatusChartPanelModelToolbarState } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { TestsPageHistorySectionProps } from '@/features/tests/pages/TestsPageHistorySection';
import type { TestsPageTabbedPanelProps } from '@/features/tests/pages/TestsPageTabbedPanel';
import type { TestRunStatus } from '@flowqa/shared';

type TestsPageExtendedContentProps = {
  groupErroredWithFailed: boolean;
  panelAvailableTags: string[];
  countedAvailableTags: MetricsToolbarTagOption[];
  tabbedPanelProps: TestsPageTabbedPanelProps;
  historySectionProps: TestsPageHistorySectionProps;
  onStatusToggle: (status: string) => void;
  sharedChartToolbarState: MetricsStatusChartPanelModelToolbarState<TestRunStatus>;
  showLineChartPoints: boolean;
  showMetricCardsSkeleton: boolean;
  suppressChartAnimation: boolean;
  testMetricCards: MetricCardItem[];
  TestsPageTabbedPanelComponent: ComponentType<TestsPageTabbedPanelProps>;
  TestsPageHistorySectionComponent: ComponentType<TestsPageHistorySectionProps>;
  testsStatusOptions: Array<TableStatusOption<TestRunStatus>>;
};

export const TestsPageExtendedContent = ({
  groupErroredWithFailed,
  panelAvailableTags: _panelAvailableTags,
  countedAvailableTags,
  tabbedPanelProps,
  historySectionProps,
  onStatusToggle,
  sharedChartToolbarState,
  showLineChartPoints,
  showMetricCardsSkeleton,
  suppressChartAnimation,
  testMetricCards,
  TestsPageTabbedPanelComponent,
  TestsPageHistorySectionComponent,
  testsStatusOptions,
}: TestsPageExtendedContentProps): ReactElement => {
  return (
    <Stack gap="2xl">
      <StackedMetricsStatusChartSection<TestRunStatus>
        key="status-timeline"
        title={getChartTitle('statusTimeline')}
        searchAriaLabel="Search stacked test chart data"
        noRowsBaseText={getEmptyStateMessage('entity.noneFound', { entity: 'testRun' })}
        scope="test"
        projectId={tabbedPanelProps.selectedProjectId}
        availableTags={countedAvailableTags}
        metricCards={showMetricCardsSkeleton ? [] : testMetricCards}
        loading={showMetricCardsSkeleton}
        statusCardSkeletonCount={groupErroredWithFailed ? 4 : 5}
        selectedStatusIds={sharedChartToolbarState.selectedStatuses}
        onStatusToggle={onStatusToggle}
        toolbarState={sharedChartToolbarState}
        statusOptions={testsStatusOptions}
        statusFilterLabel={getChartTitle('status')}
        suppressAnimation={suppressChartAnimation}
        showPoints={showLineChartPoints}
        includeLatestQuickFilter
      />
      <Stack key="history-section">
        <TestsPageHistorySectionComponent {...historySectionProps} />
      </Stack>
      <Stack key="clone-panel">
        <TestsPageTabbedPanelComponent {...tabbedPanelProps} />
      </Stack>
    </Stack>
  );
};
